import { Injectable, inject } from '@angular/core';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { ResolvedLocale, resolveLocale } from '@server/utility/locale';
import { LOCALE_STORAGE_KEY, bootLocale } from '../../locale-bootstrap';
import { AuthService } from './auth.service';
import { LibraryService } from './library.service';

// Set just before a locale reload, so a reload that doesn't land on the
// expected locale (e.g. storage was cleared mid-flight) can't loop.
const RELOAD_GUARD_KEY = 'liqLocaleReloadedTo';

/**
 * Keeps the page's date locale in step with the signed-in user's language and
 * the current library's country.
 *
 * The locale itself is applied at boot by `locale-bootstrap.ts` from a cached
 * value, because Angular's `LOCALE_ID` can't change at runtime. This service
 * re-resolves it whenever the user or library changes (login, library switch)
 * and, if the result differs from what the page booted with, caches the new
 * one and reloads — the same approach the profile modal uses for a language
 * change. Switching between libraries in the same locale does nothing.
 *
 * Named `AppLocaleService` to avoid confusion with the daterangepicker's own
 * `LocaleService`.
 */
@Injectable({ providedIn: 'root' })
export class AppLocaleService {
    private authService = inject(AuthService);
    private libraryService = inject(LibraryService);

    private started = false;

    /** The locale this page load is running with. */
    readonly current: ResolvedLocale = bootLocale;

    /** Start watching the user and library. Called once from an app initializer. */
    init(): void {
        if (this.started) return;
        this.started = true;

        combineLatest([
            this.authService.getUser().pipe(filter(Boolean)),
            this.libraryService.getCurrentLibrary().pipe(filter(Boolean))
        ])
            .pipe(
                map(([user]) => resolveLocale(user.language, this.libraryService.getLibrarySettings().countryCode)),
                distinctUntilChanged((prev, curr) => prev.angular === curr.angular)
            )
            .subscribe((locale) => this.apply(locale));
    }

    /**
     * Cache the locale for a new UI language ahead of the reload the profile
     * modal does after a language change, so that reload lands on the right
     * locale instead of needing a second one.
     */
    rememberLanguage(language: string): void {
        this.writeCachedLocale(resolveLocale(language, this.libraryService.getLibrarySettings().countryCode));
    }

    private apply(locale: ResolvedLocale): void {
        if (locale.angular === this.current.angular) {
            this.clearReloadGuard();
            return;
        }
        if (!this.writeCachedLocale(locale)) return;
        if (this.readReloadGuard() === locale.angular) return;
        this.setReloadGuard(locale.angular);
        // Deferred so the rest of this library change (e.g. persisting the
        // selected library, which a reload restores) finishes first.
        setTimeout(() => window.location.reload());
    }

    /** Returns false when storage is unavailable, in which case a reload couldn't take effect. */
    private writeCachedLocale(locale: ResolvedLocale): boolean {
        try {
            localStorage.setItem(LOCALE_STORAGE_KEY, locale.angular);
            return localStorage.getItem(LOCALE_STORAGE_KEY) === locale.angular;
        } catch {
            return false;
        }
    }

    private readReloadGuard(): string | null {
        try {
            return sessionStorage.getItem(RELOAD_GUARD_KEY);
        } catch {
            return null;
        }
    }

    private setReloadGuard(angularId: string): void {
        try {
            sessionStorage.setItem(RELOAD_GUARD_KEY, angularId);
        } catch {
            // Without session storage there is no guard; the cached-locale check above still applies.
        }
    }

    private clearReloadGuard(): void {
        try {
            sessionStorage.removeItem(RELOAD_GUARD_KEY);
        } catch {
            // Nothing to clear.
        }
    }
}
